import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, PencilSimple, Trash, ArrowsClockwise, SquaresFour, ListBullets } from "@phosphor-icons/react";
import AttachmentGallery from "@/components/AttachmentGallery";
import Thumb from "@/components/Thumb";
import ColorSwatch from "@/components/ColorSwatch";
import FabricRollQRModal from "@/components/FabricRollQRModal";
import SearchBar, { textMatch } from "@/components/SearchBar";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchPrimaryAttachments, bulkPrimaryMap } from "@/lib/attachments";

/* ---------------- shared bits ---------------- */
const inputCls = "w-full bg-transparent border-b py-2 outline-none focus:border-[#C2A878] text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="data-label mb-2">{label}</div>
      {children}
    </label>
  );
}

function PrimaryBtn({ children, testId, disabled }) {
  return (
    <button type="submit" data-testid={testId} disabled={disabled}
      className="w-full inline-flex items-center justify-center px-6 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
      style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
      {children}
    </button>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-10" data-testid="modal-overlay"
      style={{ background: "rgba(26,26,26,0.4)" }} onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-3xl" : "max-w-2xl"} mx-4 p-8 max-h-[92vh] overflow-y-auto`}
        style={{ background: "var(--nylsu-card)", border: "1px solid var(--nylsu-border)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-[#8F8D84] hover:text-[#1A1A1A]">
            <X size={18} weight="light" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
      <div>
        <div className="overline mb-3">{eyebrow}</div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-4 text-sm text-[#5C5A54] max-w-xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

function SectionCard({ children, testId }) {
  return (
    <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid={testId}>
      {children}
    </div>
  );
}

function EmptyRow({ colSpan, text }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-sm text-[#8F8D84] text-center">{text}</td>
    </tr>
  );
}

function RowActions({ onEdit, onDelete, testIdPrefix }) {
  const { t } = useI18n();
  return (
    <div className="flex gap-1 justify-end">
      {onEdit && (
        <button onClick={onEdit} data-testid={`${testIdPrefix}-edit`} className="p-2 text-[#5C5A54] hover:text-[#1A1A1A] transition-colors" aria-label={t("common.edit")}>
          <PencilSimple size={16} weight="light" />
        </button>
      )}
      {onDelete && (
        <button onClick={onDelete} data-testid={`${testIdPrefix}-delete`} className="p-2 text-[#5C5A54] hover:text-[#8C3B3B] transition-colors" aria-label={t("common.delete")}>
          <Trash size={16} weight="light" />
        </button>
      )}
    </div>
  );
}

function NewBtn({ onClick, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em]"
      style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
      <Plus size={14} weight="light" /> {label}
    </button>
  );
}

function useList(url) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { setRows((await api.get(url)).data); }
    catch (e) {
      if (![403, 404].includes(e?.response?.status)) toast.error(formatApiError(e));
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return [rows, load];
}

/* Enum options — value stored, label via tEnum */
const COLL_STATUS = ["Concept", "Development", "Sampling", "Production", "Launched", "Archived"];
const SEASONS = ["FW", "SS", "Resort", "Pre-Fall"];
const STYLE_CATEGORIES = ["Dress", "Evening Dress", "Gown", "Top", "Blouse", "Skirt", "Pants", "Jacket", "Coat", "Suit", "Accessories", "Other"];
const STYLE_STATUS = ["Concept", "Sample", "Approved Sample", "Production Ready", "In Production", "Active", "Discontinued", "Archived"];
const MAT_CATEGORIES = ["Main Fabric", "Lining", "Interlining", "Lace", "Tulle", "Embroidery", "Beads", "Crystals", "Buttons", "Zippers", "Hooks", "Thread", "Labels", "Care Labels", "Packaging", "Garment Bags", "Boxes", "Other"];
const MAT_UNITS = ["meter", "centimeter", "piece", "kilogram", "gram", "roll", "pack", "set"];
const SUPPLIER_TYPES = ["Fabric", "Accessories", "Packaging", "Atelier", "Embroidery", "Logistics", "Other"];
const ADJUST_REASONS = ["Stock Count", "Damage", "Loss", "Correction", "Production Waste", "Other"];
const LOC_TYPES = ["atelier", "showroom", "warehouse", "virtual", "store", "consignment"];
const SIZE_OPTS = ["XS", "S", "M", "L", "XL", "34", "36", "38", "40", "42", "44"];

/* ---------------- Collections ---------------- */
function CollectionForm({ initial, onSaved }) {
  const { t, tEnum } = useI18n();
  const [form, setForm] = useState({
    name: "", season: "FW", year: new Date().getFullYear(),
    status: "Development", description: "", ...initial,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, year: Number(form.year) };
      if (initial?.id) await api.patch(`/collections/${initial.id}`, payload);
      else await api.post("/collections", payload);
      toast.success(t(initial?.id ? "collections.updatedToast" : "collections.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="collection-form">
      <Field label={t("common.name")}><input required data-testid="collection-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("collections.season")}>
          <select className={inputCls} value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })}>
            {SEASONS.map((s) => <option key={s} value={s}>{tEnum("season", s)}</option>)}
          </select>
        </Field>
        <Field label={t("collections.year")}><input required type="number" className={inputCls} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></Field>
      </div>
      <Field label={t("common.status")}>
        <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {COLL_STATUS.map((s) => <option key={s} value={s}>{tEnum("collection.status", s)}</option>)}
        </select>
      </Field>
      <Field label={t("common.description")}><textarea rows={3} className={inputCls} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      <PrimaryBtn testId="collection-submit-btn">{initial?.id ? t("common.save") : t("collections.createCta")}</PrimaryBtn>
    </form>
  );
}

export function CollectionsPage() {
  const { can } = useAuth();
  const { t, tEnum, localName } = useI18n();
  const [rows, reload] = useList("/collections");
  const [modal, setModal] = useState(null);

  const del = async (row) => {
    if (!window.confirm(t("common.deleteConfirm"))) return;
    try { await api.delete(`/collections/${row.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="collections-page">
      <PageHeader eyebrow={t("collections.eyebrow")} title={t("collections.title")} subtitle={t("collections.subtitle")}
        actions={can("collection:create") && <NewBtn onClick={() => setModal("new")} label={t("collections.new")} testId="new-collection-btn" />} />
      <SectionCard testId="collections-table-card">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-6 py-3 text-left">{t("collections.season")}</th>
              <th className="data-label px-6 py-3 text-left">{t("collections.year")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.description")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={6} text={t("collections.empty")} />}
            {rows.map((c) => (
              <tr key={c.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`collection-row-${c.id}`}>
                <td className="px-6 font-serif text-lg">{localName(c)}</td>
                <td className="px-6">{tEnum("season", c.season)}</td>
                <td className="px-6 font-mono text-xs">{c.year}</td>
                <td className="px-6"><span className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--nylsu-gold)" }}>{tEnum("collection.status", c.status)}</span></td>
                <td className="px-6 text-[#5C5A54] text-xs truncate max-w-md">{c.description}</td>
                <td className="px-6"><RowActions onEdit={can("collection:edit") && (() => setModal({ edit: c }))} onDelete={can("collection:delete") && (() => del(c))} testIdPrefix={`collection-${c.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("collections.new")}><CollectionForm onSaved={() => { setModal(null); reload(); }} /></Modal>}
      {modal?.edit && <Modal onClose={() => setModal(null)} title={t("collections.edit")}><CollectionForm initial={modal.edit} onSaved={() => { setModal(null); reload(); }} /></Modal>}
    </div>
  );
}

/* ---------------- Styles ---------------- */
function StyleForm({ initial, collections, onSaved }) {
  const { t, tEnum, localName } = useI18n();
  const [form, setForm] = useState({
    style_code: "", name: "", collection_id: collections[0]?.id || "", category: "Dress",
    subcategory: "", designer: "NYLSU Studio", description: "", retail_price: 0, wholesale_price: 0,
    estimated_cost: 0, target_margin: 0.75, currency: "EUR", status: "Concept",
    variants: [], ...initial,
  });
  const addVariant = () => setForm({ ...form, variants: [...form.variants, { color: "", size: "M" }] });
  const updateVariant = (i, k, v) => { const vs = [...form.variants]; vs[i] = { ...vs[i], [k]: v }; setForm({ ...form, variants: vs }); };
  const removeVariant = (i) => setForm({ ...form, variants: form.variants.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        retail_price: Number(form.retail_price), wholesale_price: Number(form.wholesale_price),
        estimated_cost: Number(form.estimated_cost), target_margin: Number(form.target_margin),
        variants: form.variants.filter((v) => v.color && v.size),
      };
      if (initial?.id) await api.patch(`/styles/${initial.id}`, payload);
      else await api.post("/styles", payload);
      toast.success(t(initial?.id ? "styles.updatedToast" : "styles.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="style-form">
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("styles.styleCode")}><input required data-testid="style-code-input" disabled={!!initial?.id} className={inputCls} value={form.style_code} onChange={(e) => setForm({ ...form, style_code: e.target.value.toUpperCase() })} placeholder="NY26-FW-D020" /></Field>
        <Field label={t("common.name")}><input required data-testid="style-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("nav.collections")}>
          <select required className={inputCls} value={form.collection_id} onChange={(e) => setForm({ ...form, collection_id: e.target.value })}>
            {collections.map((c) => <option key={c.id} value={c.id}>{localName(c)}</option>)}
          </select>
        </Field>
        <Field label={t("common.category")}>
          <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {STYLE_CATEGORIES.map((c) => <option key={c} value={c}>{tEnum("style.category", c)}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-6">
        <Field label={`${t("common.retail")} (€)`}><input type="number" step="0.01" className={inputCls} value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} /></Field>
        <Field label={`${t("common.wholesale")} (€)`}><input type="number" step="0.01" className={inputCls} value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} /></Field>
        <Field label={`${t("common.cost")} (€)`}><input type="number" step="0.01" className={inputCls} value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} /></Field>
      </div>
      <Field label={t("common.status")}>
        <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {STYLE_STATUS.map((s) => <option key={s} value={s}>{tEnum("style.status", s)}</option>)}
        </select>
      </Field>
      <Field label={t("common.description")}><textarea rows={2} className={inputCls} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="data-label">{t("styles.variants.title")}</div>
          <button type="button" onClick={addVariant} data-testid="style-add-variant-btn" className="text-xs uppercase tracking-[0.15em] link-underline">{t("styles.variants.add")}</button>
        </div>
        <div className="space-y-3">
          {form.variants.length === 0 && <div className="text-xs text-[#8F8D84]">{t("styles.variants.hint")}</div>}
          {form.variants.map((v, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-3 items-end" data-testid={`variant-row-${i}`}>
              <input required placeholder={t("common.color")} className={inputCls} value={v.color} onChange={(e) => updateVariant(i, "color", e.target.value)} />
              <select className={inputCls} value={v.size} onChange={(e) => updateVariant(i, "size", e.target.value)}>
                {SIZE_OPTS.map((s) => <option key={s}>{s}</option>)}
              </select>
              <button type="button" onClick={() => removeVariant(i)} className="p-2 text-[#8F8D84] hover:text-[#8C3B3B]"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <PrimaryBtn testId="style-submit-btn">{initial?.id ? t("common.save") : t("styles.createCta")}</PrimaryBtn>

      <div className="pt-6 mt-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
        <div className="data-label mb-3">{t("styles.photosHeading")}</div>
        <AttachmentGallery entity="style" entityId={initial?.id} compact />
      </div>
    </form>
  );
}

export function StylesPage() {
  const { can } = useAuth();
  const { t, tEnum, localName, fmtCurrency } = useI18n();
  const [rows, reload] = useList("/styles");
  const [colls] = useList("/collections");
  const [modal, setModal] = useState(null);
  const [primaryMap, setPrimaryMap] = useState({});
  const [q, setQ] = useState("");
  const [collFilter, setCollFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("nylsu.styles.view") || "list"; } catch { return "list"; }
  });
  const collMap = Object.fromEntries(colls.map((c) => [c.id, c]));

  const filtered = useMemo(() => rows.filter((s) => {
    if (collFilter && s.collection_id !== collFilter) return false;
    if (statusFilter && s.status !== statusFilter) return false;
    if (!q.trim()) return true;
    if (textMatch(q, s.style_code, s.name, s.name_tr, s.category)) return true;
    return (s.variants || []).some((v) => textMatch(q, v.sku, v.barcode, v.color, v.size));
  }), [rows, q, collFilter, statusFilter]);

  useEffect(() => { try { localStorage.setItem("nylsu.styles.view", view); } catch {} }, [view]);

  // Bulk-fetch primary attachment per style
  useEffect(() => {
    if (rows.length === 0) return;
    (async () => {
      const list = await fetchPrimaryAttachments("style", rows.map((r) => r.id));
      setPrimaryMap(bulkPrimaryMap(list));
    })();
  }, [rows]);

  const del = async (row) => {
    if (!window.confirm(t("styles.deleteConfirm", { code: row.style_code }))) return;
    try { await api.delete(`/styles/${row.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const viewToggle = (
    <div className="flex items-center border" style={{ borderColor: "var(--nylsu-border-strong)" }} data-testid="styles-view-toggle">
      <button
        onClick={() => setView("list")}
        data-testid="styles-view-list-btn"
        className="px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2 transition-colors"
        style={{ background: view === "list" ? "#1A1A1A" : "transparent", color: view === "list" ? "var(--nylsu-page)" : "#5C5A54" }}
      >
        <ListBullets size={14} weight="light" /> {t("view.list")}
      </button>
      <button
        onClick={() => setView("grid")}
        data-testid="styles-view-grid-btn"
        className="px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2 transition-colors"
        style={{ background: view === "grid" ? "#1A1A1A" : "transparent", color: view === "grid" ? "var(--nylsu-page)" : "#5C5A54" }}
      >
        <SquaresFour size={14} weight="light" /> {t("view.grid")}
      </button>
    </div>
  );

  const actions = (
    <div className="flex items-center gap-3">
      {viewToggle}
      {can("style:create") && <NewBtn onClick={() => setModal("new")} label={t("styles.new")} testId="new-style-btn" />}
    </div>
  );

  return (
    <div data-testid="styles-page">
      <PageHeader eyebrow={t("styles.eyebrow")} title={t("styles.title")} subtitle={t("styles.subtitle")} actions={actions} />

      <SearchBar
        testId="styles-search"
        value={q}
        onChange={setQ}
        placeholder={t("styles.searchPlaceholder")}
        resultCount={`${filtered.length} / ${rows.length}`}
        filters={
          <>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("nav.collections")}</label>
            <select value={collFilter} onChange={(e) => setCollFilter(e.target.value)}
                    className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                    data-testid="styles-collection-filter">
              <option value="">{t("common.all")}</option>
              {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] ml-3">{t("common.status")}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                    data-testid="styles-status-filter">
              <option value="">{t("common.all")}</option>
              {["active", "draft", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {(q || collFilter || statusFilter) && (
              <button type="button" onClick={() => { setQ(""); setCollFilter(""); setStatusFilter(""); }}
                      className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84]"
                      data-testid="styles-clear-filters">{t("common.clearFilters")}</button>
            )}
          </>
        } />

      {view === "list" ? (
        <SectionCard testId="styles-table-card">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-6 py-3 text-left" style={{ width: 80 }}>{t("styles.thumb")}</th>
                <th className="data-label px-6 py-3 text-left">{t("styles.styleCode")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
                <th className="data-label px-6 py-3 text-left">{t("nav.collections")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.category")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.variants")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.retail")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.cost")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <EmptyRow colSpan={10} text={q || collFilter || statusFilter ? t("common.noMatches") : t("styles.empty")} />}
              {filtered.map((s) => {
                const m = s.retail_price - s.estimated_cost;
                const marg = s.retail_price ? Math.round((m / s.retail_price) * 100) : 0;
                const p = primaryMap[s.id];
                return (
                  <tr key={s.id} className="border-t h-16" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`style-row-${s.style_code}`}>
                    <td className="px-6 py-2">
                      <Thumb attachment={p} size={48} testId={`style-thumb-${s.style_code}`} alt={s.name}
                        onClick={p ? () => setModal({ edit: s }) : undefined} />
                    </td>
                    <td className="px-6 font-mono text-xs">{s.style_code}</td>
                    <td className="px-6 font-serif text-lg">{s.name}</td>
                    <td className="px-6 text-xs">{localName(collMap[s.collection_id]) || "—"}</td>
                    <td className="px-6 text-xs">{tEnum("style.category", s.category)}</td>
                    <td className="px-6 font-mono text-xs">{s.variants?.length || 0}</td>
                    <td className="px-6 text-right font-mono text-xs">{fmtCurrency(s.retail_price)}</td>
                    <td className="px-6 text-right font-mono text-xs text-[#8F8D84]">{fmtCurrency(s.estimated_cost)}</td>
                    <td className="px-6 text-xs uppercase tracking-[0.15em]" style={{ color: marg > 60 ? "var(--nylsu-success)" : "var(--nylsu-warning)" }}>
                      {tEnum("style.status", s.status)} · {marg}%
                    </td>
                    <td className="px-6"><RowActions onEdit={can("style:edit") && (() => setModal({ edit: s }))} onDelete={can("style:delete") && (() => del(s))} testIdPrefix={`style-${s.style_code}`} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6" data-testid="styles-grid">
          {filtered.length === 0 && <div className="col-span-full text-sm text-[#8F8D84] text-center py-16">{q || collFilter || statusFilter ? t("common.noMatches") : t("styles.empty")}</div>}
          {filtered.map((s) => {
            const p = primaryMap[s.id];
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => can("style:edit") && setModal({ edit: s })}
                data-testid={`style-card-${s.style_code}`}
                className="border text-left flex flex-col overflow-hidden hover:border-[#C2A878] transition-colors group"
                style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
              >
                <div className="aspect-[3/4] w-full bg-[#F4F3ED] overflow-hidden">
                  <Thumb attachment={p} size={320} testId={`style-card-thumb-${s.style_code}`} alt={s.name} />
                </div>
                <div className="p-4 flex-1 flex flex-col gap-2">
                  <div className="overline">{s.style_code}</div>
                  <div className="font-serif text-xl leading-tight tracking-tight">{s.name}</div>
                  <div className="text-xs text-[#8F8D84]">
                    {localName(collMap[s.collection_id])} · {tEnum("style.category", s.category)}
                  </div>
                  <div className="mt-auto flex items-baseline justify-between pt-2">
                    <span className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--nylsu-gold)" }}>{tEnum("style.status", s.status)}</span>
                    <span className="font-mono text-xs text-[#8F8D84]">{s.variants?.length || 0} · {fmtCurrency(s.retail_price)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("styles.new")} wide><StyleForm collections={colls} onSaved={() => { setModal(null); reload(); }} /></Modal>}
      {modal?.edit && <Modal onClose={() => setModal(null)} title={t("styles.edit", { code: modal.edit.style_code })} wide><StyleForm initial={modal.edit} collections={colls} onSaved={() => { setModal(null); reload(); }} /></Modal>}
    </div>
  );
}

/* ---------------- Materials ---------------- */
function StockEntryModal({ material, locations, defaultLocation, onClose, onSaved }) {
  const { t, tEnum } = useI18n();
  const [form, setForm] = useState({
    direction: "increase",
    quantity: "",
    location_id: material?.location_id || defaultLocation || locations[0]?.id || "",
    reason: "Opening Stock",
    notes: "",
  });
  const [current, setCurrent] = useState({ physical: 0, reserved: 0, available: 0 });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const [invR, resR] = await Promise.all([
          api.get("/inventory"),
          api.get("/reservations").catch(() => ({ data: [] })),
        ]);
        const invRows = (invR.data || []).filter((x) =>
          x.sku === material.code && x.is_material === true
          && (!form.location_id || x.location_id === form.location_id)
        );
        const physical = invRows.reduce((a, b) => a + (b.on_hand || 0), 0);
        const resRows = (resR.data || []).filter((r) =>
          r.sku === material.code
          && (r.status === "active" || !r.status)
          && (!form.location_id || r.location_id === form.location_id)
        );
        const reserved = resRows.reduce((a, b) => a + (Number(b.quantity) || 0), 0);
        setCurrent({ physical, reserved, available: Math.max(0, physical - reserved) });
      } catch { /* non-blocking */ }
    })();
  }, [material?.code, form.location_id]);
  const qty = Number(form.quantity || 0);
  const delta = form.direction === "increase" ? qty : -qty;
  const newPhysical = current.physical + delta;
  const invalidReserve = form.direction === "decrease" && newPhysical < current.reserved;
  const save = async () => {
    if (!form.location_id) { toast.error(t("stock.needLocation")); return; }
    if (qty <= 0) { toast.error(t("stock.needQty")); return; }
    if (invalidReserve) { toast.error(t("stock.belowReserved")); return; }
    setBusy(true);
    try {
      await api.post("/inventory/adjust", {
        sku: material.code,
        location_id: form.location_id,
        quantity: delta,
        reason: form.reason,
        notes: form.notes,
      });
      toast.success(t("stock.saved"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const REASONS_INCREASE = ["Opening Stock", "Manual Stock Entry", "Stock Count Correction", "Found Stock", "Sample Material Added", "Owner Supplied Material", "Other"];
  const REASONS_DECREASE = ["Stock Count Correction", "Damage", "Loss", "Waste", "Sample Usage", "Manual Correction", "Other"];
  const reasons = form.direction === "increase" ? REASONS_INCREASE : REASONS_DECREASE;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="stock-entry-modal">
      <div className="w-full max-w-lg bg-white border shadow-xl" style={{ borderColor: "var(--nylsu-border-strong)" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--nylsu-border)" }}>
          <div>
            <div className="overline">{t("stock.addStock")}</div>
            <div className="text-sm font-serif mt-1">{material.name}</div>
            <div className="text-[10px] font-mono text-[#8F8D84]">{material.code}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-[#8F8D84] hover:text-[#0A0A0A]">×</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><div className="text-[#8F8D84] uppercase tracking-[0.15em] text-[10px]">{t("stock.physical")}</div><div className="font-mono text-sm" data-testid="stock-current-physical">{current.physical} {tEnum("material.unit", material.unit)}</div></div>
            <div><div className="text-[#8F8D84] uppercase tracking-[0.15em] text-[10px]">{t("stock.reserved")}</div><div className="font-mono text-sm">{current.reserved}</div></div>
            <div><div className="text-[#8F8D84] uppercase tracking-[0.15em] text-[10px]">{t("stock.available")}</div><div className="font-mono text-sm">{current.available}</div></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-1">{t("stock.direction")}</label>
              <select className={inputCls} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value, reason: (e.target.value === "increase" ? REASONS_INCREASE : REASONS_DECREASE)[0] })} data-testid="stock-direction">
                <option value="increase">{t("stock.increase")}</option>
                <option value="decrease">{t("stock.decrease")}</option>
              </select></div>
            <div><label className="overline block mb-1">{t("common.quantity")} ({tEnum("material.unit", material.unit)})</label>
              <input type="number" step="0.01" min="0" className={inputCls} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="stock-quantity" autoFocus /></div>
          </div>
          <div><label className="overline block mb-1">{t("common.location")}</label>
            <select required className={inputCls} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} data-testid="stock-location">
              <option value="">—</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></div>
          <div><label className="overline block mb-1">{t("common.reason")}</label>
            <select className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="stock-reason">
              {reasons.map((r) => <option key={r}>{r}</option>)}
            </select></div>
          <div><label className="overline block mb-1">{t("common.notes")}</label>
            <input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="stock-notes" /></div>
          {qty > 0 && (
            <div className="p-3 border text-xs" style={{ borderColor: invalidReserve ? "var(--nylsu-danger)" : "var(--nylsu-gold)", background: invalidReserve ? "#FDF3F3" : "#FBF9F1" }} data-testid="stock-preview">
              <span className="uppercase tracking-[0.15em] text-[10px]">{t("stock.afterAdjustment")}</span>{" "}
              <span className="font-mono">{newPhysical} {tEnum("material.unit", material.unit)}</span>
              {invalidReserve && <div className="mt-1" style={{ color: "var(--nylsu-danger)" }}>{t("stock.belowReserved")}</div>}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--nylsu-border)" }}>
          <button type="button" onClick={onClose} className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] border" style={{ borderColor: "var(--nylsu-border)" }}>{t("common.cancel")}</button>
          <button type="button" onClick={save} disabled={busy || qty <= 0 || invalidReserve || !form.location_id}
                  className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] border disabled:opacity-30"
                  style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }} data-testid="stock-save">
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialForm({ initial, suppliers, locations, onSaved }) {
  const { t, tEnum } = useI18n();
  const [form, setForm] = useState({
    code: "", name: "", category: "Main Fabric", supplier_id: "", composition: "", color: "",
    color_name: "", color_hex: "",
    unit: "meter", minimum_stock: 0, reorder_point: 0, lead_time_days: 14, unit_cost: 0,
    currency: "EUR", location_id: locations[0]?.id || "", fabric_width_cm: "", gsm: "", notes: "",
    opening_stock_qty: "",
    opening_stock_location: locations[0]?.id || "",
    ...initial,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const p = { ...form };
      const openingQty = Number(p.opening_stock_qty || 0);
      const openingLoc = p.opening_stock_location || p.location_id;
      delete p.opening_stock_qty; delete p.opening_stock_location;
      ["minimum_stock", "reorder_point", "lead_time_days", "unit_cost"].forEach((k) => (p[k] = Number(p[k] || 0)));
      p.fabric_width_cm = p.fabric_width_cm ? Number(p.fabric_width_cm) : null;
      p.gsm = p.gsm ? Number(p.gsm) : null;
      if (!p.supplier_id) delete p.supplier_id;
      if (p.color_hex && !p.color_hex.startsWith("#")) p.color_hex = "#" + p.color_hex;
      let savedCode = p.code;
      if (initial?.id) await api.patch(`/materials/${initial.id}`, p);
      else { await api.post("/materials", p); savedCode = p.code; }
      // Opening stock on create only
      if (!initial?.id && openingQty > 0 && openingLoc) {
        try {
          await api.post("/inventory/adjust", {
            sku: savedCode, location_id: openingLoc, quantity: openingQty,
            reason: "Opening Stock", notes: "Initial physical count during material creation",
          });
        } catch (err) { toast.error(formatApiError(err)); }
      }
      toast.success(t(initial?.id ? "materials.updatedToast" : "materials.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="material-form">
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.code")}><input required data-testid="material-code-input" disabled={!!initial?.id} className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FAB-SILK-BLK" /></Field>
        <Field label={t("common.name")}><input required data-testid="material-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.category")}>
          <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {MAT_CATEGORIES.map((c) => <option key={c} value={c}>{tEnum("material.category", c)}</option>)}
          </select>
        </Field>
        <Field label={t("common.unit")}>
          <select className={inputCls} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            {MAT_UNITS.map((u) => <option key={u} value={u}>{tEnum("material.unit", u)}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.supplier")}>
          <select className={inputCls} value={form.supplier_id || ""} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
            <option value="">—</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label={t("common.location")}>
          <select className={inputCls} value={form.location_id || ""} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("materials.form.composition")}><input className={inputCls} value={form.composition || ""} onChange={(e) => setForm({ ...form, composition: e.target.value })} placeholder="100% Silk" /></Field>
        <Field label={t("common.color")}><input className={inputCls} value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-[1fr_1fr_120px] gap-6 items-end">
        <Field label={t("materials.form.colorName")}>
          <input className={inputCls} data-testid="material-color-name-input" value={form.color_name || ""}
            onChange={(e) => setForm({ ...form, color_name: e.target.value })} placeholder="Burgundy · Ivory · Charcoal" />
        </Field>
        <Field label={t("materials.form.colorHex")}>
          <input className={inputCls} data-testid="material-color-hex-input" value={form.color_hex || ""}
            onChange={(e) => setForm({ ...form, color_hex: e.target.value })} placeholder="#6D1F2A" maxLength={9} />
        </Field>
        <div className="pb-2 flex items-center gap-3">
          <ColorSwatch hex={form.color_hex} name={form.color_name} size={28} testId="material-color-preview" />
          <span className="text-xs text-[#8F8D84]">{t("materials.form.colorPreview")}</span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-6">
        <Field label={t("materials.form.minStock")}><input type="number" step="0.1" className={inputCls} value={form.minimum_stock} onChange={(e) => setForm({ ...form, minimum_stock: e.target.value })} /></Field>
        <Field label={t("materials.form.reorderPt")}><input type="number" step="0.1" className={inputCls} value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} /></Field>
        <Field label={t("materials.form.leadTimeDays")}><input type="number" className={inputCls} value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} /></Field>
        <Field label={t("materials.form.unitCostEur")}><input type="number" step="0.01" className={inputCls} value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></Field>
      </div>

      {!initial?.id && (
        <div className="pt-6 mt-2 border-t" style={{ borderColor: "var(--nylsu-border)" }} data-testid="opening-stock-section">
          <div className="data-label mb-3">{t("stock.openingStockHeading")}</div>
          <div className="text-xs text-[#8F8D84] mb-3">{t("stock.openingStockHint")}</div>
          <div className="grid grid-cols-2 gap-6">
            <Field label={`${t("common.quantity")} (${tEnum("material.unit", form.unit)})`}>
              <input type="number" step="0.01" min="0" className={inputCls}
                     value={form.opening_stock_qty}
                     onChange={(e) => setForm({ ...form, opening_stock_qty: e.target.value })}
                     placeholder="0 — leave blank if none yet"
                     data-testid="material-opening-qty" />
            </Field>
            <Field label={t("common.location")}>
              <select className={inputCls} value={form.opening_stock_location || ""}
                      onChange={(e) => setForm({ ...form, opening_stock_location: e.target.value })}
                      data-testid="material-opening-location">
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
          </div>
        </div>
      )}

      <PrimaryBtn testId="material-submit-btn">{initial?.id ? t("common.save") : t("materials.createCta")}</PrimaryBtn>

      <div className="pt-6 mt-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
        <div className="data-label mb-3">{t("materials.photosHeading")}</div>
        <AttachmentGallery entity="material" entityId={initial?.id} compact />
      </div>
    </form>
  );
}

export function MaterialsPage() {
  const { can } = useAuth();
  const { t, tEnum, localName, fmtCurrency } = useI18n();
  const [rows, reload] = useList("/materials");
  const [inv, reloadInv] = useList("/inventory");
  const [suppliers] = useList("/suppliers");
  const [locations] = useList("/locations");
  const [modal, setModal] = useState(null);
  const [view, setView] = useState("list");
  const [primaryMap, setPrimaryMap] = useState({});

  useEffect(() => {
    if (rows.length) {
      fetchPrimaryAttachments("material", rows.map((r) => r.id)).then((list) => setPrimaryMap(bulkPrimaryMap(list)));
    }
  }, [rows.length]);

  const stockOf = (code) => (inv.filter((r) => r.sku === code && r.is_material).reduce((a, b) => a + b.on_hand, 0)) || 0;
  const supMap = Object.fromEntries(suppliers.map((s) => [s.id, s]));

  const del = async (row) => {
    if (!window.confirm(t("materials.deleteConfirm", { code: row.code }))) return;
    try { await api.delete(`/materials/${row.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="materials-page">
      <PageHeader eyebrow={t("materials.eyebrow")} title={t("materials.title")} subtitle={t("materials.subtitle")}
        actions={
          <div className="flex items-center gap-3">
            <div className="flex border" style={{ borderColor: "var(--nylsu-border-strong)" }} data-testid="materials-view-toggle">
              <button type="button" onClick={() => setView("list")} data-testid="materials-view-list"
                className="p-2 transition-colors" style={{ background: view === "list" ? "#1A1A1A" : "transparent", color: view === "list" ? "var(--nylsu-page)" : "#5C5A54" }}
                aria-label={t("common.listView")}>
                <ListBullets size={14} weight="light" />
              </button>
              <button type="button" onClick={() => setView("grid")} data-testid="materials-view-grid"
                className="p-2 transition-colors" style={{ background: view === "grid" ? "#1A1A1A" : "transparent", color: view === "grid" ? "var(--nylsu-page)" : "#5C5A54" }}
                aria-label={t("common.gridView")}>
                <SquaresFour size={14} weight="light" />
              </button>
            </div>
            {can("material:create") && <NewBtn onClick={() => setModal("new")} label={t("materials.new")} testId="new-material-btn" />}
          </div>
        } />
      {view === "list" ? (
        <SectionCard testId="materials-table-card">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-4 py-3 text-left" style={{ width: 72 }}>{t("common.image")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.code")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
                <th className="data-label px-4 py-3 text-left" style={{ width: 60 }}>{t("common.color")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.category")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.supplier")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.stock")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.reorder")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.unitCost")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow colSpan={10} text={t("materials.empty")} />}
              {rows.map((m) => {
                const stock = stockOf(m.code);
                const low = m.reorder_point && stock < m.reorder_point;
                const p = primaryMap[m.id];
                return (
                  <tr key={m.id} className="border-t h-16" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`material-row-${m.code}`}>
                    <td className="px-4 py-2">
                      <Thumb attachment={p} size={52} testId={`material-thumb-${m.code}`} alt={m.name}
                        onClick={() => setModal({ edit: m })} />
                    </td>
                    <td className="px-6 font-mono text-xs">{m.code}</td>
                    <td className="px-6">{localName(m)}</td>
                    <td className="px-4">
                      {(m.color_hex || m.color_name) ? (
                        <ColorSwatch hex={m.color_hex} name={m.color_name || m.color} size={20} testId={`material-swatch-${m.code}`} />
                      ) : <span className="text-[#B8B5A8] text-xs">—</span>}
                    </td>
                    <td className="px-6 text-xs">{tEnum("material.category", m.category)}</td>
                    <td className="px-6 text-xs">{supMap[m.supplier_id]?.name || "—"}</td>
                    <td className="px-6 text-right font-mono text-xs" style={{ color: low ? "var(--nylsu-warning)" : undefined }}>
                      {stock} {tEnum("material.unit", m.unit)}
                    </td>
                    <td className="px-6 text-right font-mono text-xs text-[#8F8D84]">{m.reorder_point} {tEnum("material.unit", m.unit)}</td>
                    <td className="px-6 text-right font-mono text-xs">{fmtCurrency(m.unit_cost)}</td>
                    <td className="px-6 text-right">
                      {can("inventory:edit") && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setModal({ stock: m }); }}
                                className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border mr-2"
                                style={{ borderColor: "var(--nylsu-gold)", color: "var(--nylsu-gold)" }}
                                data-testid={`material-add-stock-${m.code}`}>
                          + {t("stock.addStock")}
                        </button>
                      )}
                      <RowActions onEdit={can("material:edit") && (() => setModal({ edit: m }))} onDelete={can("material:delete") && (() => del(m))} testIdPrefix={`material-${m.code}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-6" data-testid="materials-grid">
          {rows.length === 0 && <div className="col-span-full text-sm text-[#8F8D84] text-center py-16">{t("materials.empty")}</div>}
          {rows.map((m) => {
            const p = primaryMap[m.id];
            const stock = stockOf(m.code);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => can("material:edit") && setModal({ edit: m })}
                data-testid={`material-card-${m.code}`}
                className="border text-left flex flex-col overflow-hidden hover:border-[#C2A878] transition-colors group"
                style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
              >
                <div className="aspect-square w-full bg-[#F4F3ED] overflow-hidden">
                  <Thumb attachment={p} size={320} testId={`material-card-thumb-${m.code}`} alt={m.name} />
                </div>
                <div className="p-4 flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="overline flex-1">{m.code}</div>
                    {(m.color_hex || m.color_name) && <ColorSwatch hex={m.color_hex} name={m.color_name || m.color} size={16} />}
                  </div>
                  <div className="font-serif text-lg leading-tight tracking-tight">{localName(m)}</div>
                  <div className="text-xs text-[#8F8D84]">{tEnum("material.category", m.category)}</div>
                  {m.composition && <div className="text-xs text-[#5C5A54]">{m.composition}</div>}
                  <div className="mt-auto flex items-baseline justify-between pt-2">
                    <span className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--nylsu-gold)" }}>
                      {stock} {tEnum("material.unit", m.unit)}
                    </span>
                    <span className="font-mono text-xs text-[#8F8D84]">{fmtCurrency(m.unit_cost)}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("materials.new")} wide><MaterialForm suppliers={suppliers} locations={locations} onSaved={() => { setModal(null); reload(); reloadInv(); }} /></Modal>}
      {modal?.edit && <Modal onClose={() => setModal(null)} title={`${t("common.edit")} · ${modal.edit.code}`} wide><MaterialForm initial={modal.edit} suppliers={suppliers} locations={locations} onSaved={() => { setModal(null); reload(); }} /></Modal>}
      {modal?.stock && <StockEntryModal material={modal.stock} locations={locations} defaultLocation={modal.stock.location_id} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadInv(); }} />}
    </div>
  );
}

/* ---------------- Fabric Rolls ---------------- */
function FabricRollForm({ initial, materials, locations, suppliers, onSaved }) {
  const { t, localName } = useI18n();
  const fabrics = materials.filter((m) => ["Main Fabric", "Lining", "Interlining", "Lace", "Tulle"].includes(m.category));
  const [form, setForm] = useState({
    material_id: fabrics[0]?.id || "", roll_number: "", dye_lot: "",
    received_meters: 0, current_meters: 0, reserved_meters: 0,
    location_id: locations[0]?.id || "", supplier_id: "", ...initial,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const p = { ...form };
      ["received_meters", "current_meters", "reserved_meters"].forEach((k) => (p[k] = Number(p[k] || 0)));
      if (!p.supplier_id) delete p.supplier_id;
      if (initial?.id) await api.patch(`/fabric-rolls/${initial.id}`, p);
      else await api.post("/fabric-rolls", p);
      toast.success(t(initial?.id ? "rolls.updatedToast" : "rolls.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="roll-form">
      <Field label={t("rolls.fabric")}>
        <select required data-testid="roll-material-select" className={inputCls} value={form.material_id} onChange={(e) => setForm({ ...form, material_id: e.target.value })}>
          {fabrics.map((m) => <option key={m.id} value={m.id}>{m.code} — {localName(m)}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("rolls.rollNumber")}><input required data-testid="roll-number-input" className={inputCls} value={form.roll_number} onChange={(e) => setForm({ ...form, roll_number: e.target.value })} placeholder="R-2026-008" /></Field>
        <Field label={t("rolls.dyeLot")}><input className={inputCls} value={form.dye_lot || ""} onChange={(e) => setForm({ ...form, dye_lot: e.target.value })} placeholder="DL-A46" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-6">
        <Field label={`${t("rolls.received")} (m)`}><input required type="number" step="0.1" className={inputCls} value={form.received_meters} onChange={(e) => setForm({ ...form, received_meters: e.target.value, current_meters: initial?.id ? form.current_meters : e.target.value })} /></Field>
        <Field label={`${t("rolls.current")} (m)`}><input required type="number" step="0.1" className={inputCls} value={form.current_meters} onChange={(e) => setForm({ ...form, current_meters: e.target.value })} /></Field>
        <Field label={`${t("rolls.reserved")} (m)`}><input type="number" step="0.1" className={inputCls} value={form.reserved_meters} onChange={(e) => setForm({ ...form, reserved_meters: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.location")}>
          <select required className={inputCls} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label={t("common.supplier")}>
          <select className={inputCls} value={form.supplier_id || ""} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
            <option value="">—</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      </div>
      <PrimaryBtn testId="roll-submit-btn">{initial?.id ? t("common.save") : t("rolls.createCta")}</PrimaryBtn>

      <div className="pt-6 mt-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
        <div className="data-label mb-3">{t("rolls.photosHeading")}</div>
        <AttachmentGallery entity="fabric_roll" entityId={initial?.id} compact />
      </div>
    </form>
  );
}

export function FabricRollsPage() {
  const { can } = useAuth();
  const { t, localName } = useI18n();
  const [rolls, reload] = useList("/fabric-rolls");
  const [mats] = useList("/materials");
  const [locs] = useList("/locations");
  const [sups] = useList("/suppliers");
  const [modal, setModal] = useState(null);
  const [qrRoll, setQrRoll] = useState(null);
  const [rollPrimary, setRollPrimary] = useState({});
  const [matPrimary, setMatPrimary] = useState({});
  const matMap = Object.fromEntries(mats.map((m) => [m.id, m]));
  const locMap = Object.fromEntries(locs.map((l) => [l.id, l]));

  useEffect(() => {
    if (rolls.length) fetchPrimaryAttachments("fabric_roll", rolls.map((r) => r.id)).then((list) => setRollPrimary(bulkPrimaryMap(list)));
  }, [rolls.length]);
  useEffect(() => {
    if (mats.length) fetchPrimaryAttachments("material", mats.map((m) => m.id)).then((list) => setMatPrimary(bulkPrimaryMap(list)));
  }, [mats.length]);

  const thumbFor = (r) => rollPrimary[r.id] || matPrimary[r.material_id] || null;

  const del = async (row) => {
    if (!window.confirm(t("rolls.deleteConfirm", { number: row.roll_number }))) return;
    try { await api.delete(`/fabric-rolls/${row.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="fabric-rolls-page">
      <PageHeader eyebrow={t("rolls.eyebrow")} title={t("rolls.title")} subtitle={t("rolls.subtitle")}
        actions={can("fabric_roll:create") && <NewBtn onClick={() => setModal("new")} label={t("rolls.new")} testId="new-roll-btn" />} />
      <SectionCard testId="rolls-table-card">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-4 py-3 text-left" style={{ width: 72 }}>{t("common.image")}</th>
              <th className="data-label px-6 py-3 text-left">{t("rolls.rollNumber")}</th>
              <th className="data-label px-6 py-3 text-left">{t("rolls.fabric")}</th>
              <th className="data-label px-4 py-3 text-left" style={{ width: 60 }}>{t("common.color")}</th>
              <th className="data-label px-6 py-3 text-left">{t("rolls.dyeLot")}</th>
              <th className="data-label px-6 py-3 text-right">{t("rolls.received")}</th>
              <th className="data-label px-6 py-3 text-right">{t("rolls.current")}</th>
              <th className="data-label px-6 py-3 text-right">{t("rolls.reserved")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.location")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rolls.length === 0 && <EmptyRow colSpan={10} text={t("rolls.empty")} />}
            {rolls.map((r) => {
              const parent = matMap[r.material_id];
              return (
                <tr key={r.id} className="border-t h-16" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`roll-row-${r.roll_number}`}>
                  <td className="px-4 py-2">
                    <Thumb attachment={thumbFor(r)} size={52} testId={`roll-thumb-${r.roll_number}`} alt={r.roll_number}
                      onClick={() => setModal({ edit: r })} />
                  </td>
                  <td className="px-6 font-mono text-xs">{r.roll_number}</td>
                  <td className="px-6">{localName(parent) || "—"}</td>
                  <td className="px-4">
                    {(parent?.color_hex || parent?.color_name) ? (
                      <ColorSwatch hex={parent.color_hex} name={parent.color_name || parent.color} size={20} />
                    ) : <span className="text-[#B8B5A8] text-xs">—</span>}
                  </td>
                  <td className="px-6 font-mono text-xs">{r.dye_lot}</td>
                  <td className="px-6 text-right font-mono text-xs">{r.received_meters} m</td>
                  <td className="px-6 text-right font-mono text-xs">{r.current_meters} m</td>
                  <td className="px-6 text-right font-mono text-xs" style={{ color: r.reserved_meters ? "var(--nylsu-warning)" : undefined }}>{r.reserved_meters} m</td>
                  <td className="px-6 text-xs">{locMap[r.location_id]?.name || "—"}</td>
                  <td className="px-6">
                    <button type="button" onClick={() => setQrRoll(r)}
                            className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border mr-2"
                            style={{ borderColor: "var(--nylsu-gold)", color: "var(--nylsu-gold)" }}
                            data-testid={`roll-qr-${r.roll_number}`}>
                      {t("rolls.qr")}
                    </button>
                    <RowActions onEdit={can("fabric_roll:edit") && (() => setModal({ edit: r }))} onDelete={can("fabric_roll:delete") && (() => del(r))} testIdPrefix={`roll-${r.roll_number}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SectionCard>

      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("rolls.new")} wide><FabricRollForm materials={mats} locations={locs} suppliers={sups} onSaved={() => { setModal(null); reload(); }} /></Modal>}
      {modal?.edit && <Modal onClose={() => setModal(null)} title={`${t("common.edit")} · ${modal.edit.roll_number}`} wide><FabricRollForm initial={modal.edit} materials={mats} locations={locs} suppliers={sups} onSaved={() => { setModal(null); reload(); }} /></Modal>}
      {qrRoll && <FabricRollQRModal roll={qrRoll} onClose={() => setQrRoll(null)} />}
    </div>
  );
}

/* ---------------- Suppliers ---------------- */
function SupplierForm({ initial, onSaved }) {
  const { t, tEnum } = useI18n();
  const [form, setForm] = useState({
    name: "", type: "Fabric", country: "TR", city: "", contact_person: "", email: "", phone: "",
    payment_terms: "", currency: "EUR", lead_time_days: 14, notes: "", active: true, ...initial,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const p = { ...form, lead_time_days: Number(form.lead_time_days || 0) };
      if (initial?.id) await api.patch(`/suppliers/${initial.id}`, p);
      else await api.post("/suppliers", p);
      toast.success(t(initial?.id ? "suppliers.updatedToast" : "suppliers.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="supplier-form">
      <Field label={t("common.name")}><input required data-testid="supplier-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.type")}>
          <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {SUPPLIER_TYPES.map((s) => <option key={s} value={s}>{tEnum("supplier.type", s)}</option>)}
          </select>
        </Field>
        <Field label={t("common.currency")}>
          <select className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {["EUR", "TRY", "USD", "GBP"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.country")}><input required maxLength={2} className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} placeholder="IT / TR / FR" /></Field>
        <Field label={t("common.city")}><input className={inputCls} value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("suppliers.contact")}><input className={inputCls} value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
        <Field label={`${t("common.leadTime")} (${t("common.days")})`}><input type="number" className={inputCls} value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.email")}><input type="email" className={inputCls} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label={t("common.phone")}><input className={inputCls} value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
      </div>
      <Field label={t("common.notes")}><textarea rows={2} className={inputCls} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <PrimaryBtn testId="supplier-submit-btn">{initial?.id ? t("common.save") : t("suppliers.createCta")}</PrimaryBtn>

      <div className="pt-6 mt-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
        <div className="data-label mb-3">{t("suppliers.photosHeading")}</div>
        <AttachmentGallery entity="supplier" entityId={initial?.id} compact />
      </div>
    </form>
  );
}

export function SuppliersPage() {
  const { can } = useAuth();
  const { t, tEnum } = useI18n();
  const [rows, reload] = useList("/suppliers");
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const filtered = useMemo(() => rows.filter((s) => {
    if (typeFilter && s.type !== typeFilter) return false;
    if (countryFilter && s.country !== countryFilter) return false;
    return textMatch(q, s.name, s.contact_person, s.phone, s.email, s.city, s.country);
  }), [rows, q, typeFilter, countryFilter]);
  const countries = useMemo(() => Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort(), [rows]);

  const del = async (row) => {
    if (!window.confirm(t("suppliers.deleteConfirm", { name: row.name }))) return;
    try { await api.delete(`/suppliers/${row.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="suppliers-page">
      <PageHeader eyebrow={t("suppliers.eyebrow")} title={t("suppliers.title")} subtitle={t("suppliers.subtitle")}
        actions={can("supplier:create") && <NewBtn onClick={() => setModal("new")} label={t("suppliers.new")} testId="new-supplier-btn" />} />
      <SearchBar
        testId="suppliers-search"
        value={q}
        onChange={setQ}
        placeholder={t("suppliers.searchPlaceholder")}
        resultCount={`${filtered.length} / ${rows.length}`}
        filters={
          <>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("common.type")}</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                    className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                    data-testid="suppliers-type-filter">
              <option value="">{t("common.all")}</option>
              {["fabric", "accessories", "packaging", "atelier", "embroidery", "logistics", "other"].map((v) =>
                <option key={v} value={v}>{tEnum("supplier.type", v)}</option>)}
            </select>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] ml-3">{t("common.country")}</label>
            <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}
                    className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                    data-testid="suppliers-country-filter">
              <option value="">{t("common.all")}</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {(q || typeFilter || countryFilter) && (
              <button type="button" onClick={() => { setQ(""); setTypeFilter(""); setCountryFilter(""); }}
                      className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84]"
                      data-testid="suppliers-clear-filters">{t("common.clearFilters")}</button>
            )}
          </>
        } />
      <SectionCard testId="suppliers-table-card">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.type")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.country")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.city")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.leadTime")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.currency")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <EmptyRow colSpan={7} text={q || typeFilter || countryFilter ? t("common.noMatches") : t("suppliers.empty")} />}
            {filtered.map((s) => (
              <tr key={s.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`supplier-row-${s.id}`}>
                <td className="px-6 font-serif text-lg">{s.name}</td>
                <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("supplier.type", s.type)}</td>
                <td className="px-6 font-mono text-xs">{s.country}</td>
                <td className="px-6 text-xs">{s.city}</td>
                <td className="px-6 text-right font-mono text-xs">{s.lead_time_days} {t("common.days")}</td>
                <td className="px-6 font-mono text-xs">{s.currency}</td>
                <td className="px-6"><RowActions onEdit={can("supplier:edit") && (() => setModal({ edit: s }))} onDelete={can("supplier:delete") && (() => del(s))} testIdPrefix={`supplier-${s.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("suppliers.new")}><SupplierForm onSaved={() => { setModal(null); reload(); }} /></Modal>}
      {modal?.edit && <Modal onClose={() => setModal(null)} title={`${t("common.edit")} · ${modal.edit.name}`}><SupplierForm initial={modal.edit} onSaved={() => { setModal(null); reload(); }} /></Modal>}
    </div>
  );
}

/* ---------------- Inventory ---------------- */
function AdjustForm({ locations, materials, styles, onSaved }) {
  const { t, tEnum, localName } = useI18n();
  const skuOptions = [
    ...materials.map((m) => ({ sku: m.code, label: `${m.code} — ${localName(m)} (${t("inventory.tab.materials").toLowerCase()})` })),
    ...styles.flatMap((s) => (s.variants || []).map((v) => ({ sku: v.sku, label: `${v.sku} — ${s.name} · ${v.color}/${v.size}` }))),
  ];
  const [form, setForm] = useState({ sku: skuOptions[0]?.sku || "", location_id: locations[0]?.id || "", quantity: 0, reason: "Stock Count", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inventory/adjust", { ...form, quantity: Number(form.quantity) });
      toast.success(t("inventory.adjust.postedToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="adjust-form">
      <Field label={t("inventory.adjust.sku")}>
        <select required data-testid="adjust-sku-select" className={inputCls} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}>
          {skuOptions.map((o) => <option key={o.sku} value={o.sku}>{o.label}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.location")}>
          <select className={inputCls} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label={t("inventory.adjust.quantity")}>
          <input required type="number" step="0.1" data-testid="adjust-qty-input" className={inputCls} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="+50 / -12.5" />
        </Field>
      </div>
      <Field label={t("inventory.adjust.reason")}>
        <select className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
          {ADJUST_REASONS.map((r) => <option key={r} value={r}>{tEnum("inventory.reason", r)}</option>)}
        </select>
      </Field>
      <Field label={t("common.notes")}><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <PrimaryBtn testId="adjust-submit-btn">{t("inventory.adjust.submit")}</PrimaryBtn>
    </form>
  );
}

export function InventoryPage() {
  const { can } = useAuth();
  const { t, tEnum, fmtDateTime } = useI18n();
  const [snap, reloadSnap] = useList("/inventory");
  const [locations] = useList("/locations");
  const [movements, reloadMv] = useList("/inventory-movements?limit=100");
  const [materials] = useList("/materials");
  const [styles] = useList("/styles");
  const [tab, setTab] = useState("finished");
  const [openAdjust, setOpenAdjust] = useState(false);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("");

  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const skuIndex = useMemo(() => {
    const idx = {};
    for (const s of styles) for (const v of (s.variants || [])) idx[v.sku] = { ...v, style: s };
    for (const m of materials) idx[m.code] = { name: m.name, code: m.code };
    return idx;
  }, [styles, materials]);
  const filtered = useMemo(() => {
    let rows = snap.filter((r) => (tab === "finished" ? !r.is_material : r.is_material));
    if (locFilter) rows = rows.filter((r) => r.location_id === locFilter);
    if (!q.trim()) return rows;
    return rows.filter((r) => {
      const info = skuIndex[r.sku];
      return textMatch(q, r.sku, info?.style?.style_code, info?.style?.name,
                        info?.color, info?.size, info?.name, info?.code);
    });
  }, [snap, tab, locFilter, q, skuIndex]);

  return (
    <div data-testid="inventory-page">
      <PageHeader eyebrow={t("inventory.eyebrow")} title={t("inventory.title")} subtitle={t("inventory.subtitle")}
        actions={can("inventory:edit") && (
          <button onClick={() => setOpenAdjust(true)} data-testid="new-adjustment-btn"
            className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em]"
            style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
            <ArrowsClockwise size={14} weight="light" /> {t("inventory.adjustBtn")}
          </button>
        )} />
      <div className="flex gap-6 mb-6 border-b" style={{ borderColor: "var(--nylsu-border)" }}>
        {[["finished", "inventory.tab.finished"], ["materials", "inventory.tab.materials"], ["movements", "inventory.tab.movements"]].map(([k, key]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inv-tab-${k}`}
            className="pb-3 -mb-px text-xs uppercase tracking-[0.2em] transition-colors"
            style={{ color: tab === k ? "#1A1A1A" : "#8F8D84", borderBottom: tab === k ? "1px solid var(--nylsu-gold)" : "1px solid transparent" }}>
            {t(key)}
          </button>
        ))}
      </div>

      {tab !== "movements" && (
        <SearchBar
          testId="inventory-search"
          value={q}
          onChange={setQ}
          placeholder={t("inventory.searchPlaceholder")}
          resultCount={`${filtered.length}`}
          filters={
            <>
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("common.location")}</label>
              <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)}
                      className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                      data-testid="inventory-location-filter">
                <option value="">{t("common.all")}</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              {(q || locFilter) && (
                <button type="button" onClick={() => { setQ(""); setLocFilter(""); }}
                        className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84]"
                        data-testid="inventory-clear-filters">{t("common.clearFilters")}</button>
              )}
            </>
          } />
      )}

      {tab === "movements" ? (
        <SectionCard testId="inv-movements-card">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-6 py-3 text-left">{t("common.timestamp")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.type")}</th>
                <th className="data-label px-6 py-3 text-left">SKU</th>
                <th className="data-label px-6 py-3 text-left">{t("common.location")}</th>
                <th className="data-label px-6 py-3 text-right">{t("common.delta")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.reference")}</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 && <EmptyRow colSpan={6} text={t("inventory.emptyMovements")} />}
              {movements.map((m) => (
                <tr key={m.id} className="border-t h-12" style={{ borderColor: "var(--nylsu-border)" }}>
                  <td className="px-6 font-mono text-xs">{fmtDateTime(m.timestamp)}</td>
                  <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("inventory.movementType", m.movement_type)}</td>
                  <td className="px-6 font-mono text-xs">{m.sku}</td>
                  <td className="px-6 text-xs">{locMap[m.location_id]?.name || m.location_id}</td>
                  <td className="px-6 text-right font-mono text-xs" style={{ color: m.delta >= 0 ? "var(--nylsu-success)" : "var(--nylsu-danger)" }}>
                    {m.delta >= 0 ? "+" : ""}{m.delta}
                  </td>
                  <td className="px-6 text-xs text-[#8F8D84]">{m.reference}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      ) : (
        <SectionCard testId="inv-snapshot-card">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-6 py-3 text-left">{t("inventory.skuCode")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.location")}</th>
                <th className="data-label px-6 py-3 text-right">{t("inventory.onHand")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <EmptyRow colSpan={3} text={t("inventory.emptyStock")} />}
              {filtered.map((r, i) => (
                <tr key={i} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}>
                  <td className="px-6 font-mono text-xs">{r.sku}</td>
                  <td className="px-6">{locMap[r.location_id]?.name || r.location_id}</td>
                  <td className="px-6 text-right font-mono">{r.on_hand}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      {openAdjust && <Modal onClose={() => setOpenAdjust(false)} title={t("inventory.adjust.title")}><AdjustForm locations={locations} materials={materials} styles={styles} onSaved={() => { setOpenAdjust(false); reloadSnap(); reloadMv(); }} /></Modal>}
    </div>
  );
}

/* ---------------- Integrations ---------------- */
export function IntegrationsPage() {
  const { t } = useI18n();
  const [rows] = useList("/integrations");
  return (
    <div data-testid="integrations-page">
      <PageHeader eyebrow={t("integrations.eyebrow")} title={t("integrations.title")} subtitle={t("integrations.subtitle")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {rows.map((r) => (
          <div key={r.key} className="border p-8" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid={`integration-${r.key}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline mb-2">{r.key}</div>
                <h3 className="font-serif text-2xl tracking-tight">{r.name}</h3>
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 border" style={{ borderColor: "var(--nylsu-border-strong)", color: "var(--nylsu-warning)" }}>
                {t("integrations.demoBadge")}
              </span>
            </div>
            <p className="text-sm text-[#5C5A54]">{r.description}</p>
            <button className="mt-8 text-xs uppercase tracking-[0.2em] link-underline" data-testid={`integration-connect-${r.key}`}>
              {t("integrations.configureCta")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Location form ---------------- */
function LocationForm({ initial, onSaved }) {
  const { t, tEnum } = useI18n();
  const [form, setForm] = useState({
    name: "", code: "", type: "warehouse", country: "TR", city: "", active: true, ...initial,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) await api.patch(`/locations/${initial.id}`, form);
      else await api.post("/locations", form);
      toast.success(t(initial?.id ? "admin.locationUpdatedToast" : "admin.locationCreatedToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="location-form">
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.name")}><input required data-testid="location-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label={t("common.code")}><input required data-testid="location-code-input" disabled={!!initial?.id} className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="PAR-2" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("common.type")}>
          <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {LOC_TYPES.map((tk) => <option key={tk} value={tk}>{tEnum("location.type", tk)}</option>)}
          </select>
        </Field>
        <Field label={t("common.country")}><input required maxLength={2} className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} /></Field>
      </div>
      <Field label={t("common.city")}><input className={inputCls} value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
      <PrimaryBtn testId="location-submit-btn">{initial?.id ? t("common.save") : t("admin.locationCreateCta")}</PrimaryBtn>
    </form>
  );
}

/* ---------------- Admin ---------------- */
export function AdminPage() {
  const { can, user: currentUser } = useAuth();
  const { t, tEnum, fmtDateTime } = useI18n();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("users");
  const [modal, setModal] = useState(null);

  const load = async () => {
    const [u, r, l] = await Promise.all([api.get("/users"), api.get("/roles"), api.get("/locations")]);
    setUsers(u.data); setRoles(r.data); setLocations(l.data);
    try { const a = await api.get("/audit-logs?limit=100"); setAudit(a.data); } catch { setAudit([]); }
  };
  useEffect(() => { load(); }, []);

  const delUser = async (u) => {
    if (u.id === currentUser?.id) { toast.error(t("admin.cannotDeleteSelf")); return; }
    if (!window.confirm(t("admin.userDeleteConfirm", { email: u.email }))) return;
    try { await api.delete(`/users/${u.id}`); toast.success(t("common.deletedToast")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delLoc = async (l) => {
    if (!window.confirm(t("admin.locationDeleteConfirm", { name: l.name }))) return;
    try { await api.delete(`/locations/${l.id}`); toast.success(t("common.deletedToast")); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow={t("admin.eyebrow")} title={t("admin.title")} subtitle={t("admin.subtitle")} />
      <div className="flex gap-6 mb-6 border-b" style={{ borderColor: "var(--nylsu-border)" }}>
        {[["users", "admin.tab.users"], ["roles", "admin.tab.roles"], ["locations", "admin.tab.locations"], ["audit", "admin.tab.audit"]].map(([k, key]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className="pb-3 -mb-px text-xs uppercase tracking-[0.2em]"
            style={{ color: tab === k ? "#1A1A1A" : "#8F8D84", borderBottom: tab === k ? "1px solid var(--nylsu-gold)" : "1px solid transparent" }}>
            {t(key)}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          <div className="flex justify-end mb-4">
            {can("user:create") && <NewBtn onClick={() => setModal({ type: "user" })} label={t("admin.newUser")} testId="new-user-btn" />}
          </div>
          <SectionCard testId="users-table-card">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--nylsu-table-head)" }}>
                  <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.email")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.role")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.active")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`user-row-${u.email}`}>
                    <td className="px-6">{u.name}</td>
                    <td className="px-6 font-mono text-xs">{u.email}</td>
                    <td className="px-6 text-xs uppercase tracking-[0.1em]">{t(`role.${u.role}`, u.role)}</td>
                    <td className="px-6 text-xs" style={{ color: u.active ? "var(--nylsu-success)" : "var(--nylsu-danger)" }}>
                      {u.active ? t("common.active") : t("common.inactive")}
                    </td>
                    <td className="px-6"><RowActions onEdit={can("user:edit") && (() => setModal({ type: "user", edit: u }))} onDelete={can("user:delete") && (() => delUser(u))} testIdPrefix={`user-${u.email}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
          {modal?.type === "user" && (
            <Modal onClose={() => setModal(null)} title={modal.edit ? t("admin.editUser", { email: modal.edit.email }) : t("admin.inviteUser")}>
              <UserForm roles={roles} initial={modal.edit} onSaved={() => { setModal(null); load(); }} />
            </Modal>
          )}
        </>
      )}

      {tab === "roles" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((r) => (
            <div key={r.key} className="border p-6" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid={`role-card-${r.key}`}>
              <div className="overline mb-2">{r.key}</div>
              <h3 className="font-serif text-xl mb-4">{t(`role.${r.key}`, r.label)}</h3>
              <ul className="space-y-1">
                {r.permissions.map((p) => <li key={p} className="font-mono text-xs text-[#5C5A54]">· {p}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === "locations" && (
        <>
          <div className="flex justify-end mb-4">
            {can("location:create") && <NewBtn onClick={() => setModal({ type: "location" })} label={t("admin.newLocation")} testId="new-location-btn" />}
          </div>
          <SectionCard testId="locations-table-card">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--nylsu-table-head)" }}>
                  <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.code")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.type")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.country")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.city")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((l) => (
                  <tr key={l.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`location-row-${l.code}`}>
                    <td className="px-6 font-serif text-lg">{l.name}</td>
                    <td className="px-6 font-mono text-xs">{l.code}</td>
                    <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("location.type", l.type)}</td>
                    <td className="px-6 font-mono text-xs">{l.country}</td>
                    <td className="px-6 text-xs">{l.city}</td>
                    <td className="px-6"><RowActions onEdit={can("location:edit") && (() => setModal({ type: "location", edit: l }))} onDelete={can("location:delete") && (() => delLoc(l))} testIdPrefix={`location-${l.code}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
          {modal?.type === "location" && (
            <Modal onClose={() => setModal(null)} title={modal.edit ? t("admin.editLocation", { name: modal.edit.name }) : t("admin.newLocation")}>
              <LocationForm initial={modal.edit} onSaved={() => { setModal(null); load(); }} />
            </Modal>
          )}
        </>
      )}

      {tab === "audit" && (
        <SectionCard testId="audit-table-card">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--nylsu-table-head)" }}>
                <th className="data-label px-6 py-3 text-left">{t("common.timestamp")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.user")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.action")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.entity")}</th>
                <th className="data-label px-6 py-3 text-left">{t("common.entityId")}</th>
              </tr>
            </thead>
            <tbody>
              {audit.length === 0 && <EmptyRow colSpan={5} text={t("admin.auditEmpty")} />}
              {audit.map((a) => (
                <tr key={a.id} className="border-t h-12" style={{ borderColor: "var(--nylsu-border)" }}>
                  <td className="px-6 font-mono text-xs">{fmtDateTime(a.timestamp)}</td>
                  <td className="px-6 text-xs">{a.user_email}</td>
                  <td className="px-6 text-xs uppercase tracking-[0.1em]">{a.action}</td>
                  <td className="px-6 text-xs">{a.entity}</td>
                  <td className="px-6 font-mono text-[10px] text-[#8F8D84]">{a.entity_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}
    </div>
  );
}

function UserForm({ roles, initial, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ email: "", name: "", role: "management", password: "", active: true, ...initial });
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) {
        const patch = { name: form.name, role: form.role, active: form.active };
        if (pw) patch.password = pw;
        await api.patch(`/users/${initial.id}`, patch);
        toast.success(t("admin.userUpdatedToast"));
      } else {
        await api.post("/users", { email: form.email, name: form.name, role: form.role, password: form.password });
        toast.success(t("admin.userCreatedToast"));
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="user-form">
      <Field label={t("common.name")}><input required data-testid="user-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label={t("common.email")}><input required type="email" disabled={!!initial?.id} data-testid="user-email-input" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
      <Field label={t("common.role")}>
        <select className={inputCls} data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          {roles.map((r) => <option key={r.key} value={r.key}>{t(`role.${r.key}`, r.label)}</option>)}
        </select>
      </Field>
      {initial?.id ? (
        <>
          <Field label={t("common.active")}>
            <select className={inputCls} value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
              <option value="1">{t("common.active")}</option><option value="0">{t("common.inactive")}</option>
            </select>
          </Field>
          <Field label={t("admin.newPassword")}><input type="password" minLength={6} className={inputCls} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("admin.passwordHint")} /></Field>
        </>
      ) : (
        <Field label={t("common.password")}><input required type="password" minLength={6} data-testid="user-password-input" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
      )}
      <PrimaryBtn testId="user-submit-btn">{initial?.id ? t("common.save") : t("admin.userCreateCta")}</PrimaryBtn>
    </form>
  );
}

/* ---------------- BOM ---------------- */
function BOMMatThumb({ material }) {
  const [prim, setPrim] = useState(null);
  useEffect(() => {
    if (!material?.id) return;
    fetchPrimaryAttachments("material", [material.id]).then((list) => setPrim(bulkPrimaryMap(list)[material.id]));
  }, [material?.id]);
  return <Thumb attachment={prim} size={36} alt={material?.name || ""} />;
}

function BOMForm({ initial, styles, materials, onSaved }) {
  const { t, tEnum, localName } = useI18n();
  const [form, setForm] = useState({
    style_id: styles[0]?.id || "", color: "", notes: "",
    items: [{ material_id: materials[0]?.id || "", quantity_per_unit: 0, unit: "meter", waste_percent: 0 }],
    ...initial,
  });
  const addItem = () => setForm({ ...form, items: [...form.items, { material_id: materials[0]?.id || "", quantity_per_unit: 0, unit: "piece", waste_percent: 0 }] });
  const updateItem = (i, k, v) => { const it = [...form.items]; it[i] = { ...it[i], [k]: v }; setForm({ ...form, items: it }); };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: form.items.filter((x) => x.material_id).map((x) => ({
          ...x, quantity_per_unit: Number(x.quantity_per_unit), waste_percent: Number(x.waste_percent),
        })),
      };
      if (initial?.id) await api.patch(`/boms/${initial.id}`, payload);
      else await api.post("/boms", payload);
      toast.success(t(initial?.id ? "bom.updatedToast" : "bom.createdToast"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="bom-form">
      <Field label={t("bom.style")}>
        <select required data-testid="bom-style-select" disabled={!!initial?.id} className={inputCls} value={form.style_id} onChange={(e) => setForm({ ...form, style_id: e.target.value })}>
          {styles.map((s) => <option key={s.id} value={s.id}>{s.style_code} — {s.name}</option>)}
        </select>
      </Field>
      <Field label={t("bom.colorHint")}><input className={inputCls} value={form.color || ""} onChange={(e) => setForm({ ...form, color: e.target.value })} /></Field>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="data-label">{t("bom.title.header")}</div>
          <button type="button" onClick={addItem} data-testid="bom-add-item-btn" className="text-xs uppercase tracking-[0.15em] link-underline">{t("bom.addMaterial")}</button>
        </div>
        <div className="space-y-4">
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-[2fr_120px_120px_100px_auto] gap-3 items-end" data-testid={`bom-item-${i}`}>
              <select required className={inputCls} value={it.material_id} onChange={(e) => updateItem(i, "material_id", e.target.value)}>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {localName(m)}</option>)}
              </select>
              <input required type="number" step="0.001" placeholder={t("bom.qty")} className={inputCls} value={it.quantity_per_unit} onChange={(e) => updateItem(i, "quantity_per_unit", e.target.value)} />
              <select className={inputCls} value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)}>
                {MAT_UNITS.map((u) => <option key={u} value={u}>{tEnum("material.unit", u)}</option>)}
              </select>
              <input type="number" step="0.1" placeholder={t("bom.wastePlaceholder")} className={inputCls} value={it.waste_percent} onChange={(e) => updateItem(i, "waste_percent", e.target.value)} />
              <button type="button" onClick={() => removeItem(i)} className="p-2 text-[#8F8D84] hover:text-[#8C3B3B]"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <Field label={t("common.notes")}><textarea rows={2} className={inputCls} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <PrimaryBtn testId="bom-submit-btn">{initial?.id ? t("bom.saveCta") : t("bom.createCta")}</PrimaryBtn>
    </form>
  );
}

export function BOMPage() {
  const { can } = useAuth();
  const { t, tEnum, localName, fmtDateTime } = useI18n();
  const [boms, reload] = useList("/boms");
  const [styles] = useList("/styles");
  const [colls] = useList("/collections");
  const [materials] = useList("/materials");
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");
  const [collFilter, setCollFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [primaryMap, setPrimaryMap] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    if (styles.length === 0) return;
    (async () => {
      const list = await fetchPrimaryAttachments("style", styles.map((s) => s.id));
      setPrimaryMap(bulkPrimaryMap(list));
    })();
  }, [styles]);

  const bomByStyle = useMemo(() => {
    const m = {};
    for (const b of boms) if (!m[b.style_id]) m[b.style_id] = b;
    return m;
  }, [boms]);
  const bomStatus = (styleId) => {
    const b = bomByStyle[styleId];
    if (!b) return "no_bom";
    if (!b.items || b.items.length === 0) return "draft";
    return b.status || "ready";
  };
  const collMap = Object.fromEntries(colls.map((c) => [c.id, c]));

  const filtered = useMemo(() => styles.filter((s) => {
    if (collFilter && s.collection_id !== collFilter) return false;
    const status = bomStatus(s.id);
    if (statusFilter && status !== statusFilter) return false;
    if (!q.trim()) return true;
    return textMatch(q, s.style_code, s.name, s.name_tr, s.category)
      || (s.variants || []).some((v) => textMatch(q, v.sku, v.color, v.size));
  }), [styles, q, collFilter, statusFilter, bomByStyle]);

  return (
    <div data-testid="bom-page">
      <PageHeader eyebrow={t("bom.eyebrow")} title={t("bom.title")} subtitle={t("bom.subtitleList")}
        actions={can("bom:create") && <NewBtn onClick={() => setModal("new")} label={t("bom.new")} testId="new-bom-btn" />} />

      <SearchBar
        testId="bom-search"
        value={q}
        onChange={setQ}
        placeholder={t("bom.searchPlaceholder")}
        resultCount={`${filtered.length} / ${styles.length}`}
        filters={
          <>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("nav.collections")}</label>
            <select value={collFilter} onChange={(e) => setCollFilter(e.target.value)}
                    className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                    data-testid="bom-collection-filter">
              <option value="">{t("common.all")}</option>
              {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84] ml-3">{t("bom.status")}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs bg-transparent border-b py-1 outline-none focus:border-[#C2A878]"
                    data-testid="bom-status-filter">
              <option value="">{t("common.all")}</option>
              {["no_bom", "draft", "ready"].map((s) => <option key={s} value={s}>{t(`bom.status.${s}`)}</option>)}
            </select>
            {(q || collFilter || statusFilter) && (
              <button type="button" onClick={() => { setQ(""); setCollFilter(""); setStatusFilter(""); }}
                      className="text-[10px] uppercase tracking-[0.15em] underline text-[#8F8D84]"
                      data-testid="bom-clear-filters">{t("common.clearFilters")}</button>
            )}
          </>
        } />

      <SectionCard testId="bom-list-card">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left" style={{ width: 72 }}>{t("styles.thumb")}</th>
              <th className="data-label px-6 py-3 text-left">{t("styles.styleCode")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-6 py-3 text-left">{t("nav.collections")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.category")}</th>
              <th className="data-label px-6 py-3 text-left">{t("bom.status")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <EmptyRow colSpan={7} text={q || collFilter || statusFilter ? t("common.noMatches") : t("bom.empty")} />}
            {filtered.map((s) => {
              const st = bomStatus(s.id);
              const p = primaryMap[s.id];
              return (
                <tr key={s.id} className="border-t h-16 cursor-pointer hover:bg-[#F4F3ED]"
                    style={{ borderColor: "var(--nylsu-border)" }}
                    onClick={() => nav(`/bom/${s.id}`)}
                    data-testid={`bom-row-${s.style_code}`}>
                  <td className="px-6 py-2">
                    <Thumb attachment={p} size={48} alt={s.name} />
                  </td>
                  <td className="px-6 font-mono text-xs">{s.style_code}</td>
                  <td className="px-6 font-serif text-lg">{s.name}</td>
                  <td className="px-6 text-xs">{localName(collMap[s.collection_id]) || "—"}</td>
                  <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("style.category", s.category)}</td>
                  <td className="px-6">
                    <span className="uppercase text-[10px] tracking-[0.1em] px-2 py-1 border"
                          style={{
                            borderColor: st === "ready" ? "var(--nylsu-gold)" : (st === "no_bom" ? "var(--nylsu-danger)" : "var(--nylsu-border)"),
                            color: st === "ready" ? "var(--nylsu-gold)" : (st === "no_bom" ? "var(--nylsu-danger)" : "#8F8D84"),
                          }}
                          data-testid={`bom-status-${s.style_code}`}>
                      {t(`bom.status.${st}`)}
                    </span>
                  </td>
                  <td className="px-6 text-right">
                    <Link to={`/bom/${s.id}`} onClick={(e) => e.stopPropagation()}
                          className="text-[10px] uppercase tracking-[0.15em] underline"
                          data-testid={`bom-open-${s.style_code}`}>
                      {t("bom.open")}
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SectionCard>

      {modal === "new" && <Modal onClose={() => setModal(null)} title={t("bom.new")} wide><BOMForm styles={styles} materials={materials} onSaved={() => { setModal(null); reload(); }} /></Modal>}
    </div>
  );
}

/* ---------------- BOM Detail (single product) ---------------- */
export function BOMDetailPage() {
  const { styleId } = useParams();
  const { can, user } = useAuth();
  const canSeeCost = user?.role === "admin" || user?.role === "management" || user?.role === "finance"
    || (user?.permissions || []).includes("bom:view_cost") || (user?.permissions || []).includes("bom:edit");
  const canApplyPrice = user?.role === "admin" || (user?.permissions || []).includes("style:edit");
  const { t, tEnum, localName } = useI18n();
  const [boms, reload] = useList("/boms");
  const [styles] = useList("/styles");
  const [materials] = useList("/materials");
  const [primary, setPrimary] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [cost, setCost] = useState(null);
  const nav = useNavigate();

  const style = styles.find((s) => s.id === styleId);
  const bom = boms.find((b) => b.style_id === styleId);
  const matMap = Object.fromEntries(materials.map((m) => [m.id, m]));

  const loadCost = async () => {
    if (!styleId) return;
    try {
      const r = await api.get(`/styles/${styleId}/cost`);
      setCost(r.data);
    } catch (e) { /* non-blocking */ }
  };
  useEffect(() => {
    if (!styleId) return;
    fetchPrimaryAttachments("style", [styleId]).then((list) => setPrimary((list && list[0]) || null));
    loadCost();
    // eslint-disable-next-line
  }, [styleId, bom?.updated_at]);

  const applyPrice = async () => {
    if (!window.confirm(t("costing.applyConfirm", { suggested: cost?.suggested_price, current: cost?.retail_price }))) return;
    try {
      await api.post(`/styles/${styleId}/apply-suggested-price`);
      toast.success(t("costing.applied"));
      // reload style via boms refresh (will refetch cost + style via useList)
      await reload();
      await loadCost();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async () => {
    if (!bom || !window.confirm(t("bom.deleteConfirm"))) return;
    try { await api.delete(`/boms/${bom.id}`); toast.success(t("common.deletedToast")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!style) return <div data-testid="bom-detail-loading" className="text-sm text-[#8F8D84] py-16 text-center">…</div>;

  const money = (n) => `${cost?.currency === "EUR" ? "€" : cost?.currency === "TRY" ? "₺" : cost?.currency || ""} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const pct = (materials_total, total) => total > 0 ? ((materials_total / total) * 100).toFixed(1) : "0";

  return (
    <div data-testid="bom-detail-page">
      <button type="button" onClick={() => nav(-1)} className="text-xs uppercase tracking-[0.15em] underline text-[#5C5A54] hover:text-[#0A0A0A] mb-6" data-testid="bom-back">
        ← {t("bom.backToList")}
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="flex items-start gap-6 flex-wrap">
            <div style={{ width: 180 }}><Thumb attachment={primary} size={180} alt={style.name} testId="bom-detail-thumb" /></div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-3">{style.style_code}</div>
              <h1 className="font-serif font-light text-4xl tracking-tight leading-none">{style.name}</h1>
              <p className="mt-3 text-xs text-[#5C5A54] uppercase tracking-[0.15em]">{tEnum("style.category", style.category)}</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                {can("bom:edit") && bom && (
                  <button type="button" onClick={() => setEditOpen(true)} className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] border" style={{ borderColor: "var(--nylsu-gold)", color: "var(--nylsu-gold)" }} data-testid="bom-detail-edit">
                    {t("bom.editRecipe")}
                  </button>
                )}
                {can("bom:delete") && bom && (
                  <button type="button" onClick={del} className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] border" style={{ borderColor: "var(--nylsu-border)", color: "#8C3B3B" }} data-testid="bom-detail-delete">
                    {t("common.delete")}
                  </button>
                )}
                {!bom && can("bom:create") && (
                  <button type="button" onClick={() => setEditOpen(true)} className="px-4 py-2 text-[10px] uppercase tracking-[0.15em] border" style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }} data-testid="bom-detail-create">
                    {t("bom.createRecipe")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* COST & PRICING panel */}
        {canSeeCost && (
          <div className="border p-5" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="bom-cost-panel">
            <div className="overline mb-4">{t("costing.panelTitle")}</div>
            {!cost || !cost.has_bom ? (
              <div className="text-xs text-[#8F8D84]">{t("costing.noBom")}</div>
            ) : (
              <>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#5C5A54]">{t("costing.materials")}</span>
                    <span className="font-mono" data-testid="cost-materials">{money(cost.materials_total)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C5A54]">{t("costing.labor")}</span>
                    <span className="font-mono" data-testid="cost-labor">{money(cost.labor_total)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C5A54]">{t("costing.overhead")}</span>
                    <span className="font-mono" data-testid="cost-overhead">{money(cost.overhead)}</span></div>
                  <div className="flex justify-between pt-2 border-t font-serif text-lg" style={{ borderColor: "var(--nylsu-border)" }}>
                    <span>{t("costing.totalCost")}</span>
                    <span className="font-mono" data-testid="cost-total">{money(cost.total_cost)}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t text-xs text-[#8F8D84] space-y-1" style={{ borderColor: "var(--nylsu-border)" }}>
                  <div>{t("costing.materialsPct")}: {pct(cost.materials_total, cost.total_cost)}%</div>
                  <div>{t("costing.laborPct")}: {pct(cost.labor_total, cost.total_cost)}%</div>
                  <div>{t("costing.overheadPct")}: {pct(cost.overhead, cost.total_cost)}%</div>
                </div>
                <div className="mt-4 pt-4 border-t space-y-2 text-sm" style={{ borderColor: "var(--nylsu-border)" }}>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">
                    {t("costing.method")}: {cost.pricing_method === "margin" ? t("costing.margin") : t("costing.markup")}
                    {" · "}
                    {cost.pricing_method === "margin" ? `${cost.target_margin_percent}%` : `${cost.target_markup_percent}%`}
                  </div>
                  <div className="flex justify-between"><span className="text-[#5C5A54]">{t("costing.suggestedPrice")}</span>
                    <span className="font-mono font-serif text-lg" style={{ color: "var(--nylsu-gold)" }} data-testid="cost-suggested">{money(cost.suggested_price)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C5A54]">{t("costing.currentRetail")}</span>
                    <span className="font-mono" data-testid="cost-retail">{money(cost.retail_price)}</span></div>
                  {cost.current_gross_profit != null && (
                    <>
                      <div className="flex justify-between text-xs"><span className="text-[#8F8D84]">{t("costing.gpAtRetail")}</span>
                        <span className="font-mono">{money(cost.current_gross_profit)}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-[#8F8D84]">{t("costing.gmAtRetail")}</span>
                        <span className="font-mono">{cost.current_gross_margin_percent}%</span></div>
                    </>
                  )}
                </div>
                {cost.cost_status === "cost_incomplete" && (
                  <div className="mt-3 p-2 border text-[11px]" style={{ borderColor: "var(--nylsu-danger)", background: "#FDF3F3", color: "var(--nylsu-danger)" }} data-testid="cost-incomplete-warn">
                    {t("costing.incompleteWarn", { missing: (cost.missing_materials || []).join(", ") || "—" })}
                  </div>
                )}
                {canApplyPrice && cost.cost_status === "cost_ready" && Math.abs((cost.suggested_price - cost.retail_price)) > 0.5 && (
                  <button type="button" onClick={applyPrice}
                          className="mt-4 w-full px-4 py-2 text-[10px] uppercase tracking-[0.15em] border"
                          style={{ background: "var(--nylsu-gold)", borderColor: "var(--nylsu-gold)", color: "#0A0A0A" }}
                          data-testid="cost-apply-price">
                    {t("costing.applyPrice")}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {!bom ? (
        <SectionCard testId="bom-detail-empty"><div className="p-16 text-center text-sm text-[#8F8D84]">{t("bom.noRecipe")}</div></SectionCard>
      ) : (
        <>
          <SectionCard testId="bom-detail-card">
            <div className="p-4 border-b overline" style={{ borderColor: "var(--nylsu-border)" }}>{t("costing.sectionMaterials")}</div>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: "var(--nylsu-table-head)" }}>
                  <th className="data-label px-4 py-3 text-left" style={{ width: 60 }}>{t("common.image")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("bom.material")}</th>
                  <th className="data-label px-4 py-3 text-left" style={{ width: 44 }}>{t("common.color")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.code")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("bom.qtyPerUnit")}</th>
                  <th className="data-label px-6 py-3 text-left">{t("common.unit")}</th>
                  <th className="data-label px-6 py-3 text-right">{t("bom.waste")}</th>
                  {canSeeCost && <th className="data-label px-6 py-3 text-right">{t("costing.unitCost")}</th>}
                  {canSeeCost && <th className="data-label px-6 py-3 text-right">{t("costing.lineCost")}</th>}
                </tr>
              </thead>
              <tbody>
                {(bom.items || []).map((it, idx) => {
                  const m = matMap[it.material_id];
                  const line = cost?.items?.[idx];
                  return (
                    <tr key={idx} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`bom-detail-item-${idx}`}>
                      <td className="px-4 py-2"><BOMMatThumb material={m} /></td>
                      <td className="px-6">{localName(m) || "—"}</td>
                      <td className="px-4">{(m?.color_hex || m?.color_name) ? <ColorSwatch hex={m.color_hex} name={m.color_name || m.color} size={16} /> : null}</td>
                      <td className="px-6 font-mono text-xs">{m?.code || "—"}</td>
                      <td className="px-6 text-right font-mono text-xs">{it.quantity_per_unit}</td>
                      <td className="px-6 text-xs">{tEnum("material.unit", it.unit)}</td>
                      <td className="px-6 text-right font-mono text-xs text-[#8F8D84]">{it.waste_percent}%</td>
                      {canSeeCost && <td className="px-6 text-right font-mono text-xs">{line?.unit_cost ? money(line.unit_cost) : "—"}</td>}
                      {canSeeCost && <td className="px-6 text-right font-mono text-xs" style={{ color: line?.missing_cost ? "var(--nylsu-danger)" : undefined }}>{line?.missing_cost ? t("costing.missing") : money(line?.line_cost || 0)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SectionCard>

          {canSeeCost && (
            <SectionCard testId="bom-labor-card">
              <div className="p-4 border-b overline flex items-center justify-between" style={{ borderColor: "var(--nylsu-border)" }}>
                <span>{t("costing.sectionLabor")}</span>
                <span className="font-mono text-xs">{money(cost?.labor_total || 0)}</span>
              </div>
              {(bom.labor_lines || []).length === 0 ? (
                <div className="p-6 text-center text-xs text-[#8F8D84]">{t("costing.noLabor")}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr style={{ background: "var(--nylsu-table-head)" }}>
                    <th className="data-label px-6 py-3 text-left">{t("costing.laborType")}</th>
                    <th className="data-label px-6 py-3 text-left">{t("costing.atelier")}</th>
                    <th className="data-label px-6 py-3 text-left">{t("costing.operation")}</th>
                    <th className="data-label px-6 py-3 text-right">{t("common.quantity")}</th>
                    <th className="data-label px-6 py-3 text-right">{t("costing.cost")}</th>
                  </tr></thead>
                  <tbody>
                    {(bom.labor_lines || []).map((l, idx) => (
                      <tr key={idx} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`bom-labor-${idx}`}>
                        <td className="px-6 text-xs uppercase tracking-[0.1em]">{l.labor_type.replace(/_/g, " ")}</td>
                        <td className="px-6">{l.atelier_name || "—"}</td>
                        <td className="px-6">{l.operation}</td>
                        <td className="px-6 text-right font-mono text-xs">{l.quantity}</td>
                        <td className="px-6 text-right font-mono">{money((l.cost || 0) * (l.quantity || 1))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          {canSeeCost && (
            <SectionCard testId="bom-overhead-card">
              <div className="p-4 border-b overline flex items-center justify-between" style={{ borderColor: "var(--nylsu-border)" }}>
                <span>{t("costing.sectionOverhead")}</span>
                <span className="font-mono text-xs">{money(cost?.overhead || 0)}</span>
              </div>
              <div className="p-4 text-xs text-[#5C5A54]">
                <div><span className="uppercase tracking-[0.15em] text-[10px]">{t("costing.method")}:</span>{" "}
                  {bom.overhead_type === "fixed" ? `${t("costing.fixed")} · ${money(bom.overhead_value)}`
                  : bom.overhead_type === "percent" ? `${t("costing.percent")} · ${bom.overhead_value}%`
                  : t("common.none")}</div>
                {bom.overhead_description && <div className="mt-1">{bom.overhead_description}</div>}
              </div>
            </SectionCard>
          )}
        </>
      )}

      {editOpen && (
        <Modal onClose={() => setEditOpen(false)} title={bom ? t("bom.edit") : t("bom.new")} wide>
          <BOMForm initial={bom || { style_id: styleId }} styles={styles} materials={materials}
                    onSaved={() => { setEditOpen(false); reload(); loadCost(); }} />
        </Modal>
      )}
    </div>
  );
}

/* ---------------- Placeholders ---------------- */
export function ComingSoon({ titleKey, eyebrowKey, subtitleKey }) {
  const { t } = useI18n();
  return (
    <div data-testid={`coming-soon-${titleKey}`}>
      <PageHeader eyebrow={t(eyebrowKey)} title={t(titleKey)} subtitle={t(subtitleKey)} />
      <div className="border p-16 text-center" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <div className="overline mb-4">{t("coming.eyebrow")}</div>
        <h2 className="font-serif text-3xl mb-4">{t("coming.title")}</h2>
        <p className="text-sm text-[#5C5A54] max-w-lg mx-auto">{t("coming.subtitle")}</p>
      </div>
    </div>
  );
}

export const ProductsPage = StylesPage;
